import { createSignal } from "solid-js";

/**
 * Restaurante (organización) ACTIVO por usuario — estado compartido a nivel de
 * módulo para que TODAS las instancias de `useActiveContext` (una por página +
 * el switcher del header) reaccionen al mismo cambio, y persistido en
 * localStorage para sobrevivir recargas.
 *
 * La clave es un mapa `userId → orgId` (no un valor plano) porque la app es
 * multi-cuenta: cada cuenta recuerda SU restaurante activo y un switch de
 * cuenta no hereda el puntero de otra (mismo espíritu que el
 * workspace-switcher de time-tracker, endurecido para multi-cuenta).
 *
 * El puntero es solo una PREFERENCIA: `useActiveContext` lo valida contra las
 * orgs realmente sincronizadas en local y cae a la primera si ya no existe
 * (org borrada / dato viejo) — nunca bloquea, igual que el puntero de ticket
 * del POS (auto-sanación).
 */
const STORAGE_KEY = "comandero.activeRestaurantByUser";

type ActiveMap = Readonly<Record<string, string>>;

function readPersisted(): ActiveMap {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const entries = Object.entries(parsed).filter(
        (entry): entry is [string, string] => typeof entry[1] === "string",
      );
      return Object.fromEntries(entries);
    }
  } catch {
    // localStorage puede fallar (modo privado) o el JSON estar corrupto; el
    // estado en memoria basta y el fallback es "primera org".
  }
  return {};
}

const [activeMap, setActiveMap] = createSignal<ActiveMap>(readPersisted());

/** Puntero de restaurante activo del usuario (puede apuntar a una org ya inexistente). */
export function activeRestaurantIdFor(userId: string): string | null {
  return activeMap()[userId] ?? null;
}

/** Fija (y persiste) el restaurante activo del usuario. */
export function setActiveRestaurantIdFor(userId: string, orgId: string): void {
  const next = { ...activeMap(), [userId]: orgId };
  setActiveMap(next);
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Persistencia best-effort; la señal en memoria ya cambió.
  }
}
